import { useId, useMemo, useState } from 'react';
import { Input, Select, Typography } from 'antd';

import GroupHeader from './GroupHeader';
import ColorSwatch from './ColorSwatch';
import { SEARCH_GLASS } from '../icons';
import { DATA_TYPE_FAMILIES, dataTypeOptionGroups, dataTypeSearchText, dataTypes } from '../../utils/dictionary';
import { optionSearchFilter } from '../Input';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';

/**
 * THE data type picker, in two presentations over one roster
 * (utils/dictionary.js) — docs/plans/card-editor-dates-groups-filters.md
 * Part 1:
 *
 *   variant='gallery'  the empty state. Six families of tiles (glyph, label,
 *                      description), a search box on top that matches the
 *                      label, the description and the synonyms, so "dropdown"
 *                      finds Choice and "money" finds Number. Every tile is a
 *                      real <button>, so the whole thing is keyboard usable
 *                      with nothing bespoke.
 *   variant='select'   the chosen state's "Change type…" control: one antd
 *                      Select, options grouped by family, each row the glyph,
 *                      the label and the muted description, searchable by the
 *                      same words.
 *
 * Nothing here knows about elements or options: it takes a type name and
 * hands back a type name. What a type CHANGE does to the options is the
 * editors' business (config/elementOptionPolicy.js pruneOptionsForType).
 */
const NO_EXCLUDES = Object.freeze([]);

export default function DataTypePicker({
	variant = 'gallery',
	value = '',
	onChange,
	id,
	disabled = false,
	size,
	className = '',
	ariaLabel = 'Data type',
	placeholder = 'Choose a data type',
	searchPlaceholder = 'Search data types — “dropdown”, “money”, “link”…',
	autoFocusSearch = false,
	// Type names this picker does not offer (a multi add input is never a
	// file, PR 10b). A stored value of one still shows as itself.
	exclude = NO_EXCLUDES,
}) {
	const [query, setQuery] = useState('');
	// Two untyped rows can render two galleries at once: the description ids
	// the tiles point at must not collide.
	const uid = useId();

	const groups = useMemo(() => dataTypeOptionGroups()
		.map(group => ({ ...group, options: group.options.filter(option => !exclude.includes(option.value)) }))
		.filter(group => group.options.length), [exclude]);

	const families = useMemo(() => {
		const needle = query.trim().toLowerCase();
		return DATA_TYPE_FAMILIES
			.map(family => ({
				family,
				types: dataTypes.filter(type => (
					type.family === family
					&& !exclude.includes(type.name)
					&& (!needle || dataTypeSearchText(type).toLowerCase().includes(needle))
				)),
			}))
			.filter(group => group.types.length);
	}, [query, exclude]);

	if (variant === 'select') {
		return (
			<Select
				id={id}
				className={`data-type-select ${className}`.trim()}
				aria-label={ariaLabel}
				size={size}
				disabled={disabled}
				value={value || undefined}
				placeholder={placeholder}
				onChange={next => onChange && onChange(next)}
				options={groups}
				showSearch
				filterOption={optionSearchFilter}
				popupMatchSelectWidth={false}
				popupClassName='data-type-select__dropdown'
				optionRender={option => (
					<span className='data-type-option'>
						{option.data.icon ? (
							<span className='data-type-option__icon' aria-hidden='true'>{option.data.icon}</span>
						) : null}
						<span className='data-type-option__text'>
							<span className='data-type-option__label'>{option.data.label}</span>
							<span className='data-type-option__description'>{option.data.description}</span>
						</span>
					</span>
				)}
			/>
		);
	}

	return (
		<div className={`data-type-gallery ${className}`.trim()}>
			<Input
				{...NO_TEXT_ASSIST}
				className='data-type-gallery__search'
				aria-label='Search data types'
				placeholder={searchPlaceholder}
				prefix={<SEARCH_GLASS />}
				allowClear
				autoFocus={autoFocusSearch}
				value={query}
				onChange={event => setQuery(event.target.value)}
			/>
			{families.length ? families.map(group => (
				<div className='data-type-gallery__family' key={group.family}>
					<GroupHeader label={group.family} />
					<div className='data-type-gallery__tiles'>
						{group.types.map(type => (
							<button
								type='button'
								key={type.name}
								className={`data-type-tile${value === type.name ? ' data-type-tile--chosen' : ''}`}
								// The tile is NAMED by its type and DESCRIBED by
								// the line under it, rather than read out as one
								// run-on sentence with the example on the end.
								aria-label={type.label}
								aria-describedby={`${uid}-${type.name}`}
								aria-pressed={value === type.name}
								disabled={disabled}
								onClick={() => onChange && onChange(type.name)}
							>
								<span className='data-type-tile__icon' aria-hidden='true'>{type.bsIconJSX}</span>
								<span className='data-type-tile__label'>{type.label}</span>
								<span className='data-type-tile__description' id={`${uid}-${type.name}`}>{type.description}</span>
								<span className='data-type-tile__example'>
									{type.name === 'color' ? <ColorSwatch value={type.example} /> : null}
									{type.example}
								</span>
							</button>
						))}
					</div>
				</div>
			)) : (
				<Typography.Text type='secondary' className='data-type-gallery__empty'>
					No data type matches “{query.trim()}”.
				</Typography.Text>
			)}
		</div>
	);
}
